/**
 * Timezone Selector Component
 * Allows users to select which timezone to display appointments in
 */

import React from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useCalendarTimezone } from '../context/TimezoneContext';
import { TIMEZONE_DISPLAY_NAMES, TimezoneValue, TIMEZONES } from '../utils/timezone';

interface TimezoneSelectorProps {
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';
  label?: string;
  showIcon?: boolean;
  showPractitionerCount?: boolean;
  fullWidth?: boolean;
}

const TimezoneSelector: React.FC<TimezoneSelectorProps> = ({
  size = 'medium',
  variant = 'outlined',
  label = 'Display Timezone',
  showIcon = true,
  showPractitionerCount = true,
  fullWidth = false
}) => {
  const {
    displayTimezone,
    setDisplayTimezone,
    availableTimezones,
    practitionersByTimezone
  } = useCalendarTimezone();

  const handleTimezoneChange = (event: SelectChangeEvent<string>) => {
    const newTimezone = event.target.value as TimezoneValue;
    setDisplayTimezone(newTimezone);
  };

  // Show all Australian timezones, not just ones with practitioners
  const allTimezones = Object.values(TIMEZONES) as TimezoneValue[];
  
  // Sort timezones by display name
  const sortedTimezones = allTimezones.sort((a, b) => 
    TIMEZONE_DISPLAY_NAMES[a].localeCompare(TIMEZONE_DISPLAY_NAMES[b])
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {showIcon && (
        <AccessTimeIcon 
          color="primary" 
          fontSize={size === 'small' ? 'small' : 'medium'} 
        />
      )}
      
      <FormControl size={size} variant={variant} fullWidth={fullWidth} sx={{ mt: 3 }}>
        <InputLabel 
          id="timezone-selector-label"
          sx={{
            color: 'white',
            fontSize: '1.3rem',
            fontWeight: 500,
            transform: 'translate(14px, -32px) scale(0.75)',
            transformOrigin: 'top left',
            zIndex: 1,
            position: 'absolute',
            '&.Mui-focused': {
              color: 'white'
            },
            '&.MuiInputLabel-shrunk': {
              color: 'white',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              paddingX: 0.5,
              borderRadius: 0.5,
              fontSize: '1rem',
              transform: 'translate(14px, -32px) scale(0.75)'
            }
          }}
        >
          {label}
        </InputLabel>
        <Select
          labelId="timezone-selector-label"
          value={displayTimezone}
          label={label}
          onChange={handleTimezoneChange}
          sx={{ 
            minWidth: 180,
            backgroundColor: 'background.paper',
            '& .MuiSelect-select': {
              paddingY: 1.5,
              display: 'flex',
              alignItems: 'center'
            },
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.3)'
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: 'rgba(255, 255, 255, 0.5)'
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: 'white'
            }
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 300,
                minWidth: 200,
                '& .MuiMenuItem-root': {
                  paddingY: 1,
                  minHeight: 40
                }
              }
            }
          }}
        >
          {sortedTimezones.map((timezone) => {
            const practitionerCount = practitionersByTimezone[timezone]?.length || 0;
            const displayName = TIMEZONE_DISPLAY_NAMES[timezone];
            
            return (
              <MenuItem 
                key={timezone} 
                value={timezone}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  whiteSpace: 'nowrap'
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    flex: 1,
                    color: 'text.primary',
                    fontWeight: displayTimezone === timezone ? 600 : 400
                  }}
                >
                  {displayName}
                </Typography>
                {showPractitionerCount && practitionerCount > 0 && (
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      ml: 2, 
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      fontWeight: 500
                    }}
                  >
                    ({practitionerCount})
                  </Typography>
                )}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </Box>
  );
};

export default TimezoneSelector;